import { useEffect, useMemo, useRef } from "react";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { DEVTOOLS_ENABLED } from "../../../consts";
import { resolveTabForPath, type TabId } from "./tabs";

/** How a disabled tab presents and responds. */
export interface DisabledTab {
  /** The glyph the native tab bar draws instead of the tab's own, as SVG source. */
  nativeSvg?: string;
  /** Runs when the user presses the tab. Navigation blocked elsewhere does not call it. */
  onSelect?: (tabId: TabId) => void;
}

interface TabDisabledState {
  tabs: Partial<Record<TabId, DisabledTab>>;
  /**
   * Blocks every tab at once, for as long as something the whole bar depends on is in
   * flight (an organisation swap). A separate flag rather than an entry per tab: writing
   * into `tabs` would collide with the registrations `useDisableTab` owns, and restoring
   * afterwards would clear a lock that arrived during the swap.
   */
  suspended: boolean;
  setTabsSuspended: (suspended: boolean) => void;
  setTabDisabled: (id: TabId, disabled: DisabledTab) => void;
  clearTabDisabled: (id: TabId) => void;
}

/** What a tab presents while the bar is suspended and the tab has no entry of its own. */
const SUSPENDED: DisabledTab = {};

// Not persisted: an entry lives as long as the component that registered it.
export const useTabDisabledStore = create<TabDisabledState>()(
  subscribeWithSelector(
    devtools(
      (set) => ({
        tabs: {},
        suspended: false,
        setTabsSuspended: (suspended) =>
          set((state) => (state.suspended === suspended ? state : { suspended }), false, "setTabsSuspended"),
        setTabDisabled: (id, disabled) =>
          set((state) => ({ tabs: { ...state.tabs, [id]: disabled } }), false, "setTabDisabled"),
        clearTabDisabled: (id) =>
          set(
            (state) => {
              if (state.tabs[id] === undefined) return state;
              const tabs = { ...state.tabs };
              delete tabs[id];
              return { tabs };
            },
            false,
            "clearTabDisabled",
          ),
      }),
      { enabled: DEVTOOLS_ENABLED, name: "tabDisabled" },
    ),
  ),
);

/**
 * A tab's own entry wins while the bar is suspended, so a locked tab keeps its glyph and
 * its press handler; everything else is refused silently.
 */
const resolveTabDisabled = (state: TabDisabledState, id: TabId): DisabledTab | undefined =>
  state.tabs[id] ?? (state.suspended ? SUSPENDED : undefined);

/** The disabled entry for `id`, or undefined while the tab is reachable. */
const useTabDisabled = (id: TabId): DisabledTab | undefined => useTabDisabledStore((s) => resolveTabDisabled(s, id));

/** The same read outside React. */
export const getTabDisabled = (id: TabId): DisabledTab | undefined =>
  resolveTabDisabled(useTabDisabledStore.getState(), id);

/**
 * Disables `id` while the calling component is mounted, clearing it on unmount.
 * `undefined` leaves the tab reachable. `onSelect` is read through a ref, so a fresh
 * closure each render does not re-register.
 */
export function useDisableTab(id: TabId, disabled: DisabledTab | undefined): void {
  // In an effect, so a discarded render cannot leave the ref committed.
  const latest = useRef(disabled);
  useEffect(() => {
    latest.current = disabled;
  });

  const nativeSvg = disabled?.nativeSvg;
  const isDisabled = disabled !== undefined;

  const entry = useMemo<DisabledTab | undefined>(
    () => (isDisabled ? { nativeSvg, onSelect: (tabId) => latest.current?.onSelect?.(tabId) } : undefined),
    [isDisabled, nativeSvg],
  );

  useEffect(() => {
    if (entry === undefined) {
      useTabDisabledStore.getState().clearTabDisabled(id);
      return;
    }
    useTabDisabledStore.getState().setTabDisabled(id, entry);
    return () => useTabDisabledStore.getState().clearTabDisabled(id);
  }, [id, entry]);
}

/** Whether `id` is disabled. Blocks navigation without telling the tab about it. */
export function isTabDisabled(id: TabId): boolean {
  return getTabDisabled(id) !== undefined;
}

/** `isTabDisabled` for callers holding a destination rather than a tab. */
export function isPathTabDisabled(pathname: string): boolean {
  const tab = resolveTabForPath(pathname);
  return tab ? isTabDisabled(tab.id) : false;
}

/**
 * Tells `id` the user pressed it while disabled. The press sites are the native bar's
 * refused selection and the web bar's item; every other blocked route stays silent.
 */
export function reportTabPress(id: TabId): void {
  getTabDisabled(id)?.onSelect?.(id);
}

export interface DisabledTabProps {
  disabled?: boolean;
  onDisabledPress?: () => void;
}

/** The `NavBarItem` props expressing whatever the store currently says about `id`. */
export function useDisabledTabProps(id: TabId): DisabledTabProps {
  const disabled = useTabDisabled(id);
  return useMemo(
    () => (disabled === undefined ? {} : { disabled: true, onDisabledPress: () => disabled.onSelect?.(id) }),
    [id, disabled],
  );
}
